import httpClient from '@/lib/httpClient'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import useAuthStore from '@/store/useAuthStore'

const BASE = 'team/bank-feed'

export type FeedFilter =
  | 'all'
  | 'pending'
  | 'confirmed'
  | 'unmatched'
  | 'excluded'

export type MatchStatus =
  | 'suggested'
  | 'confirmed'
  | 'rejected'
  | 'manual'
  | 'excluded'
  | 'actual_only'

export interface RawLine {
  id: number
  transaction_date: string
  amount: string
  description: string | null
  reference: string | null
  type: string
  direction: 'in' | 'out'
  resolved_vault_id: number | null
  resolved_category_id: number | null
  paired_raw_transaction_id: number | null
  is_shadow: boolean
  currency_code: string | null
  is_foreign_currency: boolean
  external_account_id: number | null
  counterpart_account_id: number | null
  contact_id: number | null
}

export interface FeedRow {
  raw_transaction: RawLine
  match: {
    id: number
    status: MatchStatus
    confidence_score: number
    pf_transaction_id: number | null
    provider_changed_at: string | null
    matched_by: string | null
  }
  suggested_transaction: {
    transaction_id: number
    transaction_date: string
    description: string | null
    estimated_amount: string
    amount: string
    vault_name: string | null
    category_name: string | null
    behaviour: string
  } | null
}

export interface BankFeedResponse {
  data: FeedRow[]
  pending_count: number
  pending_by_vault: {
    vault_id: number | null
    pending: number
    last_transaction_date: string | null
  }[]
  can: { reconcile: boolean }
  /** Part-paid overdue invoices whose remaining balance needs an expected date. */
  needs_input: InvoiceNeedingDate[]
}

export interface InvoiceNeedingDate {
  id: number
  description: string | null
  due_date: string | null
  original_amount: number
  balance: number
  category_name: string | null
  direction: 'in' | 'out'
}

export interface Candidate {
  transaction_id: number
  transaction_date: string
  amount: number
  description: string | null
  vault_id: number | null
  vault_name: string | null
  category_name: string | null
  source: 'xero_invoice' | 'estimate'
  is_suggested: boolean
  confidence: 'high' | 'medium' | 'low'
  days_apart: number
  variance: number
}

const KEY = 'bank-feed'

export function useBankFeed(filter: FeedFilter, vaultId: number | null) {
  const teamId = useAuthStore((state) => state.currentTeamId)

  return useQuery<BankFeedResponse>({
    queryKey: [KEY, 'list', teamId, filter, vaultId],
    queryFn: async () =>
      (
        await httpClient.get(BASE, {
          params: { filter, ...(vaultId ? { vault_id: vaultId } : {}) },
        })
      ).data,
    enabled: !!teamId,
  })
}

/** Pending count for the navigation badge; light enough to poll. */
export function useBankFeedPendingCount({ enabled }: { enabled: boolean }) {
  const teamId = useAuthStore((state) => state.currentTeamId)

  return useQuery<number>({
    queryKey: [KEY, 'summary', teamId],
    queryFn: async () =>
      (await httpClient.get(`${BASE}/summary`)).data.pending_count,
    enabled: enabled && !!teamId,
    refetchInterval: 120_000,
  })
}

export function useCandidates(rawId: number | null, days: number) {
  return useQuery<{ data: Candidate[]; window_days: number }>({
    queryKey: [KEY, 'candidates', rawId, days],
    queryFn: async () =>
      (
        await httpClient.get(`${BASE}/${rawId}/candidates`, {
          params: { days },
        })
      ).data,
    enabled: rawId !== null,
  })
}

function errorMessage(error: any, fallback: string) {
  const errors = error?.response?.data?.errors
  return (
    (errors ? (Object.values(errors)[0] as string[])[0] : null) ??
    error?.response?.data?.message ??
    fallback
  )
}

function useFeedMutation<T>(
  request: (vars: T) => Promise<any>,
  success?: (data: any) => string | null,
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: request,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [KEY] })
      // A reconciled line changes balances and transactions everywhere.
      queryClient.invalidateQueries({ queryKey: ['vaults'] })
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
      const message = success?.(data)
      if (message) toast.success(message)
    },
    onError: (error) =>
      toast.error(
        errorMessage(error, 'Something went wrong. Please try again.'),
      ),
  })
}

export function useConfirmLine() {
  return useFeedMutation(
    (rawId: number) => httpClient.post(`${BASE}/${rawId}/confirm`),
    () => 'Matched',
  )
}

export function useChooseEstimate() {
  return useFeedMutation(
    ({ rawId, transactionId }: { rawId: number; transactionId: number }) =>
      httpClient.post(`${BASE}/${rawId}/choose`, {
        pf_transaction_id: transactionId,
      }),
    () => 'Matched',
  )
}

export function useRejectLine() {
  return useFeedMutation((rawId: number) =>
    httpClient.post(`${BASE}/${rawId}/reject`),
  )
}

export function useExcludeLine() {
  return useFeedMutation(
    (rawId: number) => httpClient.post(`${BASE}/${rawId}/exclude`),
    () => 'Excluded',
  )
}

export function useCreateActual() {
  return useFeedMutation(
    ({
      rawId,
      ...body
    }: {
      rawId: number
      vault_id: number
      transaction_category_id: number
      transaction_date: string
      amount: number
      description: string | null
    }) => httpClient.post(`${BASE}/${rawId}/create`, body),
    () => 'Transaction created',
  )
}

export function useBulkFeedAction() {
  return useFeedMutation(
    ({ action, rawIds }: { action: 'confirm' | 'exclude'; rawIds: number[] }) =>
      httpClient.post(`${BASE}/bulk`, { action, raw_transaction_ids: rawIds }),
    (response) => {
      const { done, failed } = response.data as {
        done: number[]
        failed: { id: number; message: string }[]
      }
      if (failed.length)
        toast.warning(`${failed.length} line(s) skipped: ${failed[0].message}`)
      return done.length ? `${done.length} line(s) updated` : null
    },
  )
}

export function useConfirmHighConfidence() {
  return useFeedMutation(
    () => httpClient.post(`${BASE}/confirm-high-confidence`),
    (response) =>
      `${response.data.confirmed} high-confidence match(es) confirmed`,
  )
}

/** When the rest of a part-paid overdue invoice is expected: forecasts the balance. */
export function useScheduleInvoiceBalance() {
  return useFeedMutation(
    ({
      estimateId,
      date,
      amount,
    }: {
      estimateId: number
      date: string
      amount: number
    }) =>
      httpClient.post(`${BASE}/invoices/${estimateId}/expected`, {
        transaction_date: date,
        amount,
      }),
    () => 'Balance forecast',
  )
}

/** Forecast the refund of an overpayment/prepayment line. */
export function useRefundEstimate() {
  return useFeedMutation(
    ({
      rawId,
      ...body
    }: {
      rawId: number
      transaction_date: string
      amount: number
      vault_id: number
      transaction_category_id: number
    }) => httpClient.post(`${BASE}/${rawId}/refund`, body),
    () => 'Refund forecast',
  )
}
